import { useState } from 'react';
import { Input } from '@/components/cms/ui/input';
import { toast } from 'sonner';
import { useVideoUpload } from '@/hooks/useVideoUpload';
import LureoButton from '@/common/LureoButton';
import { VideoIcon, TrashIcon } from '@phosphor-icons/react';
import { IconButton } from '@/components/ui/icon-button';
import { shownUrl } from '@/utils/localCopies';

interface UIVideoUploadProps {
  onVideoSelect: (videoUrl: string) => void;
  currentVideo?: string;
  className?: string;
  buttonText?: string;
  accept?: string;
  maxSizeInMB?: number;
  contextId?: string;
}

export function UIVideoUpload({
  onVideoSelect,
  currentVideo,
  className = '',
  buttonText = 'Upload Video',
  accept = 'video/mp4,video/webm,video/quicktime,video/x-msvideo',
  maxSizeInMB = 500,
  contextId
}: UIVideoUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [videoRemoved, setVideoRemoved] = useState(false);
  
  const { fileInputRef, tempVideo, tempFile, isUploading, error, handleFileSelect, uploadFile, reset } = useVideoUpload(contextId, {
    maxSizeInMB,
    autoUpload: false
  });

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await handleFileSelect(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveVideo = () => {
    reset();
    onVideoSelect('');
    setVideoRemoved(true);
    toast.success('Video removed');
  };

  const handleUpload = async () => {
    if (!tempFile) {
      toast.error('Please select a video file first');
      return;
    }

    const result = await uploadFile(tempFile);
    if (result.success && result.videoUrl) {
      onVideoSelect(result.videoUrl);
      toast.success('Video uploaded successfully');
      reset();
    } else {
      toast.error(result.error || 'Upload failed');
    }
  };

  // A video this tab uploaded plays from the file already in the browser
  const displayVideo = tempVideo || (currentVideo && !videoRemoved ? shownUrl(currentVideo) : undefined);

  return (
    <div className={`space-y-4 ${className}`}>
      <Input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
      />

      {displayVideo ? (
        <div className="space-y-3">
          <div className="relative group w-full aspect-video rounded-lureo-radius-lg overflow-hidden bg-lureo-neutral border-lureo-border-width border-lureo-line">
            <video
              src={displayVideo}
              controls
              className="w-full h-full object-contain"
              preload="metadata"
            />
            <div className="absolute right-2 top-2 z-20 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-lureo-duration-moderate">
              <IconButton label="Remove video" tone="danger" onClick={handleRemoveVideo} disabled={isUploading}>
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
          
          <div className="flex gap-2">
            <LureoButton
              variant="outline-3"
              onClick={handleButtonClick}
              className="h-9 flex-1 rounded-lureo-radius-button-pill px-5"
            >
              Replace Video
            </LureoButton>
            {tempFile && (
              <LureoButton
                variant="secondary"
                onClick={handleUpload}
                disabled={isUploading}
                className="h-9 flex-1 rounded-lureo-radius-button-pill px-4"
              >
                {isUploading ? 'Uploading...' : 'Upload Video'}
              </LureoButton>
            )}
          </div>
        </div>
      ) : (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`
            relative border-lureo-upload-line-width border-dashed rounded-lureo-radius-lg p-8 text-center transition-colors
            ${dragActive ? 'border-lureo-sky-ink bg-lureo-sky/10' : 'border-lureo-line-strong bg-lureo-neutral-subtle'}
            ${error ? 'border-lureo-danger bg-lureo-danger/5' : ''}
          `}
        >
          <VideoIcon className="h-12 w-12 mx-auto mb-4 text-lureo-ink-subtle" />
          <p className="text-lureo-14 text-lureo-ink-subtle mb-2 leading-5">
            Drag and drop a video file here, or click to select
          </p>
          <p className="text-lureo-12 text-lureo-ink-subtle mb-4 leading-4">
            Supported formats: MP4, WebM, MOV, AVI (max {maxSizeInMB}MB)
          </p>
          <LureoButton
            variant="secondary"
            onClick={handleButtonClick}
            disabled={isUploading}
            className="h-9 rounded-lureo-radius-button-pill px-4"
          >
            {buttonText}
          </LureoButton>
          {error && (
            <p className="text-lureo-14 text-lureo-danger-ink mt-2 leading-5">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}

